import httpClient from "./httpClient";

const prefix = "/incident";

export type FindIncidentsFilters = {
  branch: number;
  date?: string;
  partnerId?: number | null;
  startDate?: number | null; 
  endDate?: number | null;   
};

export async function findIncidents(
  branchOrFilters: number | FindIncidentsFilters,
  date?: string
) {
  if (typeof branchOrFilters === "number") {
    return (
      await httpClient.get(`${prefix}/?branchId=${branchOrFilters}&date=${date}`)
    ).data;
  }

  const filters = branchOrFilters as FindIncidentsFilters;
  const params = new URLSearchParams();
  params.set("branchId", String(filters.branch));
  if (filters.date) params.set("date", String(filters.date));
  if (filters.partnerId) params.set("partnerId", String(filters.partnerId));
  if (filters.startDate) params.set("startDate", String(filters.startDate));
  if (filters.endDate) params.set("endDate", String(filters.endDate));

  return (await httpClient.get(`${prefix}/?${params.toString()}`)).data;
}

export async function findIncidentsRange(
  branchId: number,
  startDate: string,
  endDate: string,
  partnerId?: number | null,
  allBranches = false,
) {
  const params = new URLSearchParams();
  params.set('branchId', String(branchId));
  params.set('startDate', startDate);
  params.set('endDate', endDate);
  if (partnerId) params.set('partnerId', String(partnerId));
  if (allBranches) params.set('allBranches', 'true');
  return (await httpClient.get(`${prefix}/range?${params.toString()}`)).data;
}

export async function findPartnerNoProgramadosPayroll(
  partnerId: number,
  branchId: number,
  startDate: string,
  endDate: string,
  allBranches = false,
) {
  const params = new URLSearchParams();
  params.set("partnerId", String(partnerId));
  params.set("branchId", String(branchId));
  params.set("startDate", startDate);
  params.set("endDate", endDate);
  if (allBranches) params.set("allBranches", "true");
  return (
    await httpClient.get(`${prefix}/partner-no-programados?${params.toString()}`)
  ).data;
}

export async function findIncidentsDirectBoss(
  bossId: number,
  date: string
) {
  const params = new URLSearchParams();
  params.set("date", date);
  return (await httpClient.get(`${prefix}/direct-boss/${bossId}?${params.toString()}`)).data;
}

export async function saveIncident(body: any) {
  return (await httpClient.post(`${prefix}/save-incident`, body)).data;
}

export async function releaseTeamIncident(body: any) {
  return (await httpClient.post(`${prefix}/release-team`, body)).data;
}

export async function releaseUnscheduledTeamIncident(body: any) {
  return (await httpClient.post(`${prefix}/release-unscheduled-team`, body)).data;
}

export async function saveUnscheduleIncident(body: any) {
  return (await httpClient.post(`${prefix}/save-unscheduled-incident`, body))
    .data;
}

export async function markTeamIncidentDayClose(date: string) {
  return (await httpClient.post(`${prefix}/day-close/team`, { date })).data;
}

export async function markIncidentDayClose(body: {
  date: string;
  scope?: "branch" | "direct_boss";
  branchId?: number;
  directBossPartnerId?: number;
}) {
  return (await httpClient.post(`${prefix}/day-close`, body)).data;
}

export async function findIncidentsBySchedule(id: number) {
  return (await httpClient.get(`${prefix}/schedule/${id}`)).data;
}

export async function findPendingIncidentsBySchedule(id: number) {
  return (await httpClient.get(`${prefix}/schedule/${id}/pending`)).data;
}

export async function findIncidentsBySchedules(
  scheduleIds: number[],
  options?: {
    includePendingLeaderReview?: boolean;
    partnerId?: number;
    branchId?: number;
    startDate?: string;
    endDate?: string;
    allBranches?: boolean;
  }
) {
  return (
    await httpClient.post(`${prefix}/schedules`, {
      scheduleIds,
      includePendingLeaderReview: options?.includePendingLeaderReview === true,
      partnerId: options?.partnerId,
      branchId: options?.branchId,
      startDate: options?.startDate,
      endDate: options?.endDate,
      allBranches: options?.allBranches === true,
    })
  ).data;
}

export type PartnerIncidentCountSnapshot = {
  pending: number;
  resolved: number;
  total: number;
  /** Filas visibles en nómina (misma lógica que TotalIncidents). */
  displayTotal?: number;
  extraDates: string[];
  pendingDates: string[];
  noProgramadoDates?: string[];
};

export async function findIncidentPartnerCount(
  partnerId: number,
  branchId: number,
  startDate: string,
  endDate: string,
  signal?: AbortSignal
): Promise<PartnerIncidentCountSnapshot> {
  const params = new URLSearchParams();
  params.set('partnerId', String(partnerId));
  params.set('branchId', String(branchId));
  params.set('startDate', startDate);
  params.set('endDate', endDate);
  const data = (
    await httpClient.get(`${prefix}/partner-count?${params.toString()}`, {
      signal,
    })
  ).data;
  return {
    ...data,
    pendingDates: data.pendingDates || [],
    noProgramadoDates: data.noProgramadoDates || [],
  };
}

const INCIDENT_BATCH_CHUNK_SIZE = 40;

function normalizeIncidentCountsBatch(
  raw: Record<string, PartnerIncidentCountSnapshot>
): Record<number, PartnerIncidentCountSnapshot> {
  const out: Record<number, PartnerIncidentCountSnapshot> = {};
  for (const [key, val] of Object.entries(raw || {})) {
    const id = Number(key);
    if (!Number.isFinite(id)) continue;
    out[id] = {
      ...val,
      pendingDates: val.pendingDates || [],
      noProgramadoDates: val.noProgramadoDates || [],
      extraDates: val.extraDates || [],
    };
  }
  return out;
}

/** Batch por POST en trozos (evita URL enorme y fallos con 50+ partners). */
export async function findIncidentPartnerCountsBatch(
  branchId: number,
  startDate: string,
  endDate: string,
  partnerIds: number[],
  signal?: AbortSignal
): Promise<Record<number, PartnerIncidentCountSnapshot>> {
  if (!partnerIds.length) {
    return {};
  }

  const unique = [...new Set(partnerIds.filter((id) => id > 0))];
  const merged: Record<number, PartnerIncidentCountSnapshot> = {};

  for (let i = 0; i < unique.length; i += INCIDENT_BATCH_CHUNK_SIZE) {
    const chunk = unique.slice(i, i + INCIDENT_BATCH_CHUNK_SIZE);
    const raw = (
      await httpClient.post(
        `${prefix}/partner-counts-batch`,
        {
          branchId,
          startDate,
          endDate,
          partnerIds: chunk,
          allBranches: true,
        },
        { signal }
      )
    ).data as Record<string, PartnerIncidentCountSnapshot>;
    Object.assign(merged, normalizeIncidentCountsBatch(raw));
  }

  return merged;
}

export async function updateFinalStatus(id: string, finalStatus: boolean) {
  return (
    await httpClient.patch(`${prefix}/update-final-status/${id}`, {
      finalStatus,
    })
  ).data;
}

export async function updateIncident(id: string, body: any) {
  return (await httpClient.patch(`${prefix}/${id}`, body)).data;
}

export interface QuickAnswer {
  id: number;
  text: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateQuickAnswerDto {
  text: string;
  isActive?: boolean;
}

export interface UpdateQuickAnswerDto {
  text?: string;
  isActive?: boolean;
}

export async function findAllQuickAnswers(): Promise<QuickAnswer[]> {
  return (await httpClient.get(`${prefix}/quick-answers`)).data;
}

export async function createQuickAnswer(data: CreateQuickAnswerDto): Promise<QuickAnswer> {
  return (await httpClient.post(`${prefix}/quick-answers`, data)).data;
}

export async function updateQuickAnswer(id: number, data: UpdateQuickAnswerDto): Promise<QuickAnswer> {
  return (await httpClient.patch(`${prefix}/quick-answers/${id}`, data)).data;
}

export async function deleteQuickAnswer(id: number): Promise<void> {
  return (await httpClient.delete(`${prefix}/quick-answers/${id}`)).data;
}